import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import StyledDataGrid from "styles/styledDataGrid";
import api from "api";
import PolicyFilter from "./Filter/PolicyFilter";

const ViewPoliciesPopup = ({
  open,
  handleClose,
  reloadParent,
  releaseKey,
  title,
  policies,
  canUpdatePolicies,
}) => {
  const [loading, setLoading] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(null);
  const [modifiedPolicies, setModifiedPolicies] = useState(); // Track policies in the grid
  const [deletedPolicies, setDeletedPolicies] = useState([]); // Track deleted policy keys
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [filteredPolicies, setFilteredPolicies] = useState(policies);

  useEffect(() => {
    if (open) setModifiedPolicies(policies);
  }, [policies, open]);

  useEffect(() => {
    const applyFilter = () => {
      if (selectedFilter === "All") {
        setFilteredPolicies(modifiedPolicies);
        return;
      }

      if (selectedFilter === "Other") {
        setFilteredPolicies(
          modifiedPolicies.filter((policy) =>
            ["ICD-10", "HCPCS", "Variance", "Retire"].includes(
              policy.categoryOfWorkName
            )
          )
        );
        return;
      }

      if (selectedFilter === "BreakFix") {
        setFilteredPolicies(
          modifiedPolicies.filter(
            (policy) => policy.categoryOfWorkName === "BreakFix"
          )
        );
        return;
      }

      // Filtering for PLA & CAB by year and quarter
      const [category, year, quarter] = selectedFilter.split(" - ");
      setFilteredPolicies(
        modifiedPolicies.filter(
          (policy) =>
            policy.categoryOfWorkName === category &&
            policy.year.toString() === year &&
            policy.quarter === quarter
        )
      );
    };

    applyFilter();
  }, [selectedFilter, modifiedPolicies]);

  const handleDisplayMessageClose = () => {
    setDisplayMessage(null);
  };

  const deletePolicy = (policy) => {
    setModifiedPolicies((prev) =>
      prev.filter((p) => p.policyKey !== policy.policyKey)
    );
    setFilteredPolicies((prev) =>
      prev.filter((p) => p.policyKey !== policy.policyKey)
    );
    setDeletedPolicies((prev) => [...prev, policy.policyKey]);
  };

  const handleBeforeClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setDeletedPolicies([]);
    setFilteredPolicies([]);
    setModifiedPolicies([]);
    setSelectedFilter("All");
    handleClose();
  };

  const handleConfirm = async () => {
    if (deletedPolicies.length === 0) {
      handleClose();
      return;
    }

    setLoading(true);
    try {
      const response = await api.ReleaseSchedule.deletePoliciesFromRelease(
        releaseKey,
        deletedPolicies
      );
      reloadParent();
      setDisplayMessage({
        message: response.data.message || "Policy Deleted successfully",
        title: "Success",
      });
      handleBeforeClose();
    } catch (error) {
      setDisplayMessage({
        message:
          error.response?.data?.errors ||
          "Failed to delete policies. Please try again.",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "policyNumber", headerName: "Policy Number", flex: 1 },
    { field: "policyTitleName", headerName: "Title", flex: 2 },
    { field: "versionDisplay", headerName: "Version", flex: 1 },
    {
      field: "quarterYear",
      headerName: "Quarter",
      flex: 1,
      valueGetter: (params, row) => `${row.quarter} ${row.year}`,
    },
    { field: "categoryOfWorkName", headerName: "Category", flex: 1 },
    { field: "pdccabStatus", headerName: "PDC/CAB", flex: 1 },
    { field: "codingStatus", headerName: "Coding", flex: 1 },
    { field: "translationStatus", headerName: "Translation", flex: 1 },
    { field: "configurationStatus", headerName: "Configuration", flex: 1 },
  ];

  // Conditionally add "Actions" column if `canUpdatePolicies` is true
  if (canUpdatePolicies) {
    columns.push({
      field: "actions",
      headerName: "",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          color="info"
          onClick={() => deletePolicy(params.row)}
        >
          REMOVE
        </Button>
      ),
    });
  }

  return (
    <Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {displayMessage && (
        <Dialog open onClose={handleDisplayMessageClose}>
          <DialogTitle>{displayMessage.title}</DialogTitle>
          <DialogContent>{displayMessage.message}</DialogContent>
          <DialogActions>
            <Button onClick={handleDisplayMessageClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={open} onClose={handleBeforeClose} maxWidth="lg" fullWidth>
        <DialogTitle fontWeight={"bold"}>{title}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "500px",
          }}
        >
          <PolicyFilter
            availablePolicies={modifiedPolicies}
            onFilterChange={setSelectedFilter}
          />
          <div style={{ flexGrow: 1, overflow: "auto" }}>
            <StyledDataGrid
              rows={filteredPolicies}
              getRowId={(row) => row.policyKey}
              columns={columns}
              rowHeight={40}
              disableColumnMenu
              hideFooter
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                },
                "& .bold-text": {
                  fontWeight: "bold",
                },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            CANCEL
          </Button>
          {canUpdatePolicies && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? "Saving..." : "CONFIRM & CONTINUE"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewPoliciesPopup;
